/**
 * Create or edit an opt-in feature.
 *
 * Two blocks, and the split is the point. **Identificação** is what the feature *is* — the BU, the
 * key, the use cases, and whether it is on at all; every one of those is chosen deliberately and
 * none of them can arrive from a pasted document. **Textos** is what people read, edited as fields
 * or as JSON, in whichever language.
 *
 * The exit questionnaire closes the second block, and it is **read-only here on purpose**: the link
 * is written on the survey, so a selector would make this form save into another module. The
 * reasoning is in `OptInExitSurveyBlock`.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { UTButton, UTSelect, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { OptInExitSurveyBlock } from './Components/OptInExitSurveyBlock';
import { OptInModalPreview } from './Components/OptInModalPreview';
import type { OptInPreviewSurface } from './Components/OptInModalPreview';
import { OptInTextsBlock } from './Components/OptInTextsBlock';
import { useExitSurveys } from '../../Hooks/useExitSurveys';
import { useOptInFeatureForm } from '../../Hooks/useOptInFeatureForm';
import { useRestoreOptInFeature } from '../../Hooks/useRestoreOptInFeature';
import { useTenantUseCases } from '../../Hooks/useTenantUseCases';
import type { OptInFeature } from '../../Types/OptInFeatures.types';
import { useTenants } from '@/Hooks/useTenants';
import { RestoreHolderDialog } from '@/Shared/components/RestoreHolderDialog/RestoreHolderDialog';
import { Switch } from '@/Shared/components/Switch/Switch';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

/**
 * Wide enough for two fields plus their previews side by side, and no wider.
 *
 * Past this the short fields end up stranded at opposite edges of the row and the eye has to
 * travel to pair a label with the box below it. A wide screen is room to put related things next
 * to each other, not licence to stretch a button label across it.
 */
const layoutStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  max-width: 1040px;
`;

const panelStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-xs);
  padding: var(--space-ml);
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  min-width: 0;
`;

const panelHeadStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const panelTitleStyle = css`
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--letter-spacing-tight);
`;

const panelHintStyle = css`
  margin: 0;
  flex: 1 1 260px;
  min-width: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const fieldsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--space-s) var(--space-m);

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const fieldLabelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

const hintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const killSwitchRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-m);
  padding: var(--space-m);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
`;

const switchTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const switchLabelStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const switchHintStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-base);
  flex-wrap: wrap;
  padding-bottom: var(--space-m);
`;

/**
 * Why the save button is off, next to the button.
 *
 * The pendências panel is further up and a long cadastro scrolls it out of sight; a disabled
 * button with no reason on screen reads as a bug.
 */
const blockedStyle = css`
  flex: 1 1 320px;
  min-width: 0;
  font-size: var(--font-size-xs);
  color: var(--gl-yellow-dark);
  text-wrap: pretty;
`;

export interface OptInFeatureFormProps {
  /** The feature being edited. Absent on `/opt-in-features/new`. */
  feature?: OptInFeature;
  onDone: () => void;
}

export const OptInFeatureForm = ({ feature, onDone }: OptInFeatureFormProps): JSX.Element => {
  const { tenants, tenantOptions } = useTenants();
  const {
    form,
    language,
    setLanguage,
    isEditing,
    isSaving,
    errors,
    setField,
    setKey,
    setText,
    setAppearance,
    setToast,
    problems,
    canSave,
    blockedReason,
    json,
    jsonError,
    setJson,
    save,
    keyHeldByDeleted,
    dismissKeyConflict,
  } = useOptInFeatureForm({ feature, onSaved: onDone });

  const restoreFeature = useRestoreOptInFeature();

  /*
    Only so the opt-out preview can tell the two modals apart: one that asks questions and one that
    just confirms. The query is the ④ block's, shared by key.
  */
  const { served: servedExitSurvey } = useExitSurveys(feature?.id);

  /** Which modal is being previewed, if any. View state: it changes nothing in the cadastro. */
  const [previewing, setPreviewing] = useState<OptInPreviewSurface | undefined>();

  const tenantSelectOptions = useMemo(
    () =>
      tenantOptions.map(option => ({
        id: String(option.id),
        text: option.text,
        value: String(option.value),
      })),
    [tenantOptions],
  );

  const { useCaseOptions } = useTenantUseCases({
    tenants,
    tenantSlug: form.tenantSlug,
  });

  const selectedTenant = tenantSelectOptions.filter(option => option.value === form.tenantSlug);
  const selectedUseCases = useCaseOptions.filter(option =>
    form.useCaseIds.includes(String(option.value)),
  );

  return (
    <MainContent
      id='opt-in-feature-form-main'
      styling={{ noBackgroundColor: true, noBorder: true }}
    >
      <div className={layoutStyle}>
        <section className={panelStyle}>
          <div className={panelHeadStyle}>
            <h2 className={panelTitleStyle}>Identificação</h2>
            <p className={panelHintStyle}>
              A chave é o que o dashboard procura no bootstrap, e não muda depois de criada.
            </p>
          </div>

          <div className={fieldsGridStyle}>
            <div className={fieldStyle} style={{ gridColumn: 'span 4' }}>
              <span className={fieldLabelStyle}>BU</span>
              {isEditing ? (
                <UTTextField id='opt-in-feature-tenant' value={form.tenantSlug} disabled />
              ) : (
                <UTSelect
                  options={tenantSelectOptions}
                  canSelectMultiple={false}
                  hasCheckbox={false}
                  defaultSelectedOptions={selectedTenant}
                  inputOptions={{
                    textFieldProps: {
                      id: 'opt-in-feature-tenant',
                      placeholder: 'Escolha a BU',
                    },
                  }}
                  onSelectedOptionsChange={options => {
                    setField('tenantSlug', options.length > 0 ? String(options[0].value) : '');
                  }}
                />
              )}
              {errors.tenantSlug ? <span className={errorStyle}>{errors.tenantSlug}</span> : null}
            </div>

            <div className={fieldStyle} style={{ gridColumn: 'span 4' }}>
              <span className={fieldLabelStyle}>Chave</span>
              <UTTextField
                id='opt-in-feature-key-input'
                placeholder='mdm-menu'
                value={form.key}
                disabled={isEditing}
                state={errors.key ? 'error' : 'normal'}
                onChange={event => {
                  setKey(event.target.value);
                }}
              />
              {errors.key ? (
                <span className={errorStyle}>{errors.key}</span>
              ) : (
                <span className={hintStyle}>
                  {isEditing ? 'Imutável depois de criada.' : 'Minúsculas e hífen.'}
                </span>
              )}
            </div>

            <div className={fieldStyle} style={{ gridColumn: 'span 4' }}>
              <span className={fieldLabelStyle}>Use cases</span>
              <UTSelect
                key={`use-cases-${form.tenantSlug}-${useCaseOptions.length}`}
                options={useCaseOptions}
                canSelectMultiple
                hasCheckbox
                defaultSelectedOptions={selectedUseCases}
                inputOptions={{
                  textFieldProps: {
                    id: 'opt-in-feature-use-cases',
                    placeholder: form.tenantSlug ? 'Todos os use cases' : 'Escolha a BU primeiro',
                  },
                }}
                onSelectedOptionsChange={options => {
                  setField(
                    'useCaseIds',
                    options.map(option => String(option.value)),
                  );
                }}
              />
              <span className={hintStyle}>
                {form.useCaseIds.length === 0
                  ? 'Vazio: oferecida a todos os use cases da BU.'
                  : `Oferecida a ${form.useCaseIds.length} de ${useCaseOptions.length} use cases.`}
              </span>
            </div>
          </div>

          <div className={killSwitchRowStyle}>
            <Switch
              id='opt-in-feature-active'
              checked={form.isActive}
              aria-label='Funcionalidade ativa'
              onChange={checked => {
                setField('isActive', checked);
              }}
            />
            <div className={switchTextStyle}>
              <span className={switchLabelStyle}>
                {form.isActive ? 'Funcionalidade ativa' : 'Funcionalidade desativada'}
              </span>
              <span className={switchHintStyle}>
                Isto é um kill switch, não um rascunho: ao desativar, a funcionalidade sai do
                bootstrap e <strong>todo mundo volta ao comportamento antigo na hora</strong>, na
                próxima carga do dashboard. O estado de quem tinha ligado fica guardado e volta se
                você reativar.
              </span>
            </div>
          </div>
        </section>

        <section className={panelStyle}>
          <OptInTextsBlock
            form={form}
            language={language}
            json={json}
            jsonError={jsonError}
            problems={problems}
            nameError={errors.name}
            onLanguageChange={setLanguage}
            onTextChange={setText}
            onAppearanceChange={setAppearance}
            onToastChange={setToast}
            onJsonChange={setJson}
            onPreview={setPreviewing}
          />

          {/*
            Read and shortcut, never a selector: the link lives on the survey, so choosing one here
            would make saving a feature write into another module — see the block's own header.
          */}
          <OptInExitSurveyBlock
            featureId={feature?.id}
            featureKey={form.key}
            tenantSlug={form.tenantSlug}
            isFeatureActive={form.isActive}
          />
        </section>

        <div className={footerStyle}>
          {blockedReason === null ? null : (
            <span id='opt-in-feature-blocked' className={blockedStyle}>
              {blockedReason}
            </span>
          )}
          <UTButton
            fitContent
            variant='secondary'
            id='opt-in-feature-cancel'
            text='Cancelar'
            onClick={onDone}
          />
          <UTButton
            fitContent
            id='opt-in-feature-submit'
            text={isEditing ? 'Salvar alterações' : 'Criar funcionalidade'}
            loading={isSaving}
            disabled={!canSave}
            title={blockedReason ?? undefined}
            onClick={save}
          />
        </div>
      </div>

      {/*
        The key is held by a DELETED feature. Offering the restore here is the point of the
        conflict carrying an id: "escolha outra chave" would leave the good name locked inside a
        row nobody can see, and the next person would hit the same wall.
      */}
      {keyHeldByDeleted ? (
        <RestoreHolderDialog
          identifierNoun='chave'
          identifier={form.key}
          entityNoun='funcionalidade'
          deletedAt={keyHeldByDeleted.deletedAt}
          isRestoring={restoreFeature.isPending}
          note={
            <>
              A funcionalidade restaurada volta <strong>como estava</strong>, ativa ou não, e cada
              pessoa recebe de volta a escolha que já tinha feito.
            </>
          }
          onClose={dismissKeyConflict}
          onRestore={() => {
            restoreFeature.mutate(keyHeldByDeleted.deletedFeatureId, {
              onSuccess: () => {
                dismissKeyConflict();
                // Back to the listing, where the restored row is now visible and live again.
                onDone();
              },
            });
          }}
        />
      ) : null}

      {/* GLModal keeps its content in the a11y tree when closed, so mount it only when needed. */}
      {previewing ? (
        <OptInModalPreview
          surface={previewing}
          texts={form.texts[language]}
          hasExitQuestionnaire={Boolean(servedExitSurvey)}
          onClose={() => {
            setPreviewing(undefined);
          }}
        />
      ) : null}
    </MainContent>
  );
};
